import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from './config'
import type { User } from '../../types/user'

const googleProvider = new GoogleAuthProvider()

// Convert Firebase User to our User type
export function mapFirebaseUser(firebaseUser: FirebaseUser): User {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
  }
}

// Create or update user document in Firestore
async function createUserDocument(user: FirebaseUser): Promise<void> {
  const userRef = doc(db, 'users', user.uid)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    await setDoc(userRef, {
      displayName: user.displayName || '',
      email: user.email,
      photoURL: user.photoURL || '',
      createdAt: serverTimestamp(),
    })
  }
}

// Sign up with email and password
export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<User> {
  const { user } = await createUserWithEmailAndPassword(auth, email, password)

  if (displayName) {
    await updateProfile(user, { displayName })
  }

  await createUserDocument(user)
  return mapFirebaseUser(user)
}

// Sign in with email and password
export async function signInWithEmail(
  email: string,
  password: string
): Promise<User> {
  const { user } = await signInWithEmailAndPassword(auth, email, password)
  return mapFirebaseUser(user)
}

// Sign in with Google
export async function signInWithGoogle(): Promise<User> {
  const { user } = await signInWithPopup(auth, googleProvider)
  await createUserDocument(user)
  return mapFirebaseUser(user)
}

// Sign out
export async function signOut(): Promise<void> {
  await firebaseSignOut(auth)
}

// Reset password
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email)
}

// Subscribe to auth state changes
export function subscribeToAuthChanges(
  callback: (user: User | null) => void
): () => void {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      callback(null)
      return
    }

    const user = mapFirebaseUser(firebaseUser)

    // Fetch role from Firestore user doc
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        if (data.role) {
          user.role = data.role
        }
      }
    } catch {
      // Silently fail — role defaults to undefined (non-admin)
    }

    callback(user)
  })
}
